import { useEffect, useState, useCallback, useRef } from "react";
import QRCode from "qrcode";
import {
  fetchCurrentUser,
  fetchDeploymentIdentity,
  fetchStudioSummary,
  fetchStudioSchedulingOptions,
  fetchPastStudioEvents,
  createStudioEvent,
  fetchEventTimer,
  studioStartEvent,
  studioPauseEvent,
  studioResumeEvent,
  studioFinishEvent,
  studioCheckInClient,
  fetchZoneAmbientOperation,
  startZoneAmbientOperation,
  stopZoneAmbientOperation,
  probeCapability,
  recoverCapability,
  logout,
  fetchNotificationChannels,
  fetchNotificationSubscriptions,
  fetchNotificationAccount,
  createNotificationAccount,
  createNotificationSubscription,
  revokeNotificationSubscription,
  runStudioDoctor,
} from "../api/client";
import SpatialSpaces from "./SpatialSpaces";
import ReceptionistCapabilityControls from "./ReceptionistCapabilityControls";
import CurrentClassEmptyState from "./CurrentClassEmptyState";
import { canViewCapabilitySurface } from "../capabilityControls";
import { resolveBranding } from "../presentationBranding";
import { resolvePortalHeader } from "../portalHeader";
import { PUBLIC_ENTRY_PATH } from "../authRouting";
import AccessAndAccounts from "./AccessAndAccounts";
import {
  formatAttendanceSummary,
  formatScheduleAttendance,
  getDominantStudioState,
} from "../studioConsolePresentation";
import {
  canConnectNotification,
  isSafeNotificationUri,
  normalizeNotificationAccountStatus,
  notificationSubscriptionUri,
} from "../notificationLifecycle";
import { computeProfileCompletion } from "../profileCompletion";
import DeploymentStamp from "./DeploymentStamp";
import StudioApplications from "./StudioApplications";
import StudioCockpit from "./StudioCockpit";
import useStudioApplications from "../useStudioApplications";

// ──────────────────────────────────────────────
// Utilities
// ──────────────────────────────────────────────

function label(value, empty = "—") {
  if (value === null || value === undefined || value === "") return empty;
  if (typeof value === "object") return empty; // guard against [object Object]
  return String(value).replaceAll("_", " ");
}

function displayState(value) {
  return label(value, "UNKNOWN").toUpperCase();
}

function formatTime(value) {
  if (!value || typeof value === "object") return "Time not scheduled";
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return "Time not scheduled";
    return d.toLocaleString([], { dateStyle: "short", timeStyle: "short" });
  } catch {
    return "Time not scheduled";
  }
}

function formatElapsed(seconds) {
  if (!seconds || seconds < 0) return "00:00:00";
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return [h, m, s].map((v) => String(v).padStart(2, "0")).join(":");
}

function stateTone(value) {
  const s = displayState(value);
  if (["READY", "HEALTHY", "RUNNING", "ACTIVE", "CHECKED IN"].includes(s)) return "ready";
  if (["FAILED", "UNHEALTHY", "BLOCKED", "FAILED"].includes(s)) return "danger";
  if (["UNKNOWN", "NOT READY", "NOT_READY", "PAUSED", "CLOSED"].includes(s)) return "attention";
  return "neutral";
}

function hasRole(user, ...roles) {
  if (!user) return false;
  const userRoles = (user.roles || []).map((r) => r.toUpperCase());
  return roles.some((r) => userRoles.includes(r.toUpperCase()));
}

function canCheckIn(user) {
  return hasRole(user, "RECEPTIONIST", "MANAGER", "ADMIN", "OWNER");
}

function canScheduleClasses(user) {
  return hasRole(user, "RECEPTIONIST", "MANAGER", "ADMIN", "OWNER");
}

function canControlClass(user) {
  return hasRole(user, "TRAINER");
}

function canAdminActions(user) {
  return hasRole(user, "PLATFORM_ADMIN", "ADMIN", "MANAGER", "OWNER");
}

// ──────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────

function StatusPill({ state, tone }) {
  const t = tone || stateTone(state);
  return (
    <span className={`studio-pill studio-pill-${t}`}>
      <span className="studio-status-dot" aria-hidden="true" />
      {displayState(state)}
    </span>
  );
}

function CapabilityRow({ name, value, onProbe, onRecover, userCanAdmin }) {
  const health = (value && (value.health || (value.online === true ? "HEALTHY" : value.online === false ? "UNHEALTHY" : "UNKNOWN"))) || "UNKNOWN";
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const handleProbe = async () => {
    setBusy(true);
    setMsg("");
    try {
      const res = await probeCapability(name);
      setMsg(`Probed: ${res.health || "??"}`);
      if (onProbe) onProbe();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleRecover = async () => {
    setBusy(true);
    setMsg("");
    try {
      const res = await recoverCapability(name);
      setMsg(res.outcome ? `Recovery: ${res.outcome.replaceAll("_", " ")}` : `Recovered: ${res.health || "HEALTHY"}`);
      if (onRecover) onRecover();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="studio-capability-row">
      <div className="studio-cap-info">
        <span className="studio-cap-name">{label(name)}</span>
        <StatusPill state={health} />
      </div>
      <div className="studio-cap-actions">
        {health === "UNKNOWN" && userCanAdmin && (
          <button className="studio-btn-xs" onClick={handleProbe} disabled={busy}>
            {busy ? "Probing…" : "Probe"}
          </button>
        )}
        {health === "UNHEALTHY" && userCanAdmin && (
          <button className="studio-btn-xs studio-btn-xs-recover" onClick={handleRecover} disabled={busy}>
            {busy ? "Recovering…" : "Recover"}
          </button>
        )}
        {msg && <span className="studio-cap-msg">{msg}</span>}
      </div>
    </li>
  );
}

function RosterRow({ clientId, isCheckedIn, eventId, userCanCheckIn, onCheckedIn }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const handleCheckIn = async () => {
    setBusy(true);
    setError("");
    try {
      await studioCheckInClient(eventId, clientId);
      if (onCheckedIn) onCheckedIn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="studio-roster-row">
      <span className={`studio-roster-indicator ${isCheckedIn ? "studio-roster-in" : "studio-roster-out"}`}>
        {isCheckedIn ? "✓" : "○"}
      </span>
      <span className="studio-roster-name">{label(clientId, "Client")}</span>
      {isCheckedIn ? (
        <span className="studio-roster-status-badge">CHECKED IN</span>
      ) : userCanCheckIn && eventId ? (
        <button className="studio-checkin-btn" onClick={handleCheckIn} disabled={busy}>
          {busy ? "…" : "CHECK IN"}
        </button>
      ) : (
        <span className="studio-roster-status-badge studio-roster-pending">NOT CHECKED IN</span>
      )}
      {error && <span className="studio-cap-msg">{error}</span>}
    </li>
  );
}

// ──────────────────────────────────────────────
// Timer hook
// ──────────────────────────────────────────────

function useClassTimer(eventId, eventState) {
  const [timerData, setTimerData] = useState(null);
  const [displaySeconds, setDisplaySeconds] = useState(0);
  const [timerKnown, setTimerKnown] = useState(false);
  const intervalRef = useRef(null);
  const tickRef = useRef(null);

  useEffect(() => {
    setTimerData(null);
    setDisplaySeconds(0);
    setTimerKnown(false);
  }, [eventId]);

  const fetchTimer = useCallback(async () => {
    if (!eventId || !["RUNNING", "PAUSED", "STARTED"].includes(eventState)) return;
    const data = await fetchEventTimer(eventId);
    const elapsed = data?.elapsed_seconds;
    if (data?.start_timestamp && elapsed !== null && elapsed !== undefined && elapsed !== "" && Number.isFinite(Number(elapsed))) {
      setTimerData(data);
      setDisplaySeconds(Math.max(0, Number(elapsed)));
      setTimerKnown(true);
    }
  }, [eventId, eventState]);

  useEffect(() => {
    fetchTimer();
    if (["RUNNING", "STARTED"].includes(eventState)) {
      intervalRef.current = setInterval(fetchTimer, 5000);
    }
    return () => clearInterval(intervalRef.current);
  }, [eventId, eventState, fetchTimer]);

  // Local tick for smooth display
  useEffect(() => {
    if (eventState === "RUNNING" || eventState === "STARTED") {
      tickRef.current = setInterval(() => {
        setDisplaySeconds((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(tickRef.current);
    }
    return () => clearInterval(tickRef.current);
  }, [eventState]);

  return { timerData, displaySeconds, timerKnown };
}

// ──────────────────────────────────────────────
// Main StudioConsole
// ──────────────────────────────────────────────

const FALLBACK_SUMMARY = {
  state_authoritative: true,
  branding: { platform: { name: "HACaMS" }, deployment: {}, attribution: {} },
  studio: { id: null, name: "Studio", lifecycle: "UNKNOWN" },
  class: { id: null, type: null, state: "NO_CLASS", trainer: null, scheduled_clients: 0, checked_in_clients: 0, roster: [], checked_in: [] },
  next_class: {},
  readiness: { ready: false, state: "UNKNOWN", blocking_reasons: [], capabilities: {} },
  incidents: [],
  fallback: { mode: "manual", reason: null },
  available_actions: [],
  schedule: [],
};

export default function StudioConsole() {
  const [summary, setSummary] = useState(FALLBACK_SUMMARY);
  const [user, setUser] = useState(null);
  const [deploymentIdentity, setDeploymentIdentity] = useState(null);
  const {
    applications,
    applicationsLoading,
    applicationsError,
    applicationsStale,
  } = useStudioApplications(Boolean(user));
  const [error, setError] = useState("");
  const [summaryRefreshing, setSummaryRefreshing] = useState(false);
  const [summaryRefreshedAt, setSummaryRefreshedAt] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState("");
  const [zoneOperation, setZoneOperation] = useState(null);
  const [zoneOperationBusy, setZoneOperationBusy] = useState(false);
  const [zoneOperationError, setZoneOperationError] = useState("");
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [doctorResult, setDoctorResult] = useState(null);
  const [doctorError, setDoctorError] = useState("");
  const [doctorRunning, setDoctorRunning] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [schedulingOptions, setSchedulingOptions] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    event_type: "",
    playlist_id: "",
    scheduled_start: "",
    scheduled_end: "",
    trainer_id: "",
    client_ids: [],
    room_id: "",
    zone_id: "",
  });
  const [scheduleBusy, setScheduleBusy] = useState(false);
  const [scheduleError, setScheduleError] = useState("");
  const [scheduleMessage, setScheduleMessage] = useState("");
  const [showPastClasses, setShowPastClasses] = useState(false);
  const scheduleFormRef = useRef(null);
  const [pastClasses, setPastClasses] = useState([]);
  const [pastClassesBusy, setPastClassesBusy] = useState(false);
  const [pastClassesError, setPastClassesError] = useState("");
  const [notificationChannels, setNotificationChannels] = useState([]);
  const [notificationSubscriptions, setNotificationSubscriptions] = useState([]);
  const [notificationPublicUrl, setNotificationPublicUrl] = useState("");
  const [notificationAccount, setNotificationAccount] = useState({ account: null, status: "not_configured" });
  const [notificationsBusy, setNotificationsBusy] = useState(false);
  const [notificationsStale, setNotificationsStale] = useState(false);
  const [notificationRefreshError, setNotificationRefreshError] = useState("");
  const [notificationsError, setNotificationsError] = useState("");
  const [accountForm, setAccountForm] = useState({ username: "", password: "", confirm_password: "" });
  const [accountBusy, setAccountBusy] = useState(false);
  const [accountError, setAccountError] = useState("");
  const [accountSuccess, setAccountSuccess] = useState("");
  const [selectedChannelId, setSelectedChannelId] = useState("");
  const [notificationInviteUris, setNotificationInviteUris] = useState({});
  const [notificationQrData, setNotificationQrData] = useState({});
  const [notificationActionBusy, setNotificationActionBusy] = useState({});
  const schedulePanelRef = useRef(null);
  const checkInPanelRef = useRef(null);

  // Load data
  const refresh = useCallback(async () => {
    setSummaryRefreshing(true);

    try {
      const next = await fetchStudioSummary();

      if (next) {
        setSummary(next);
        setError("");
        setSummaryRefreshedAt(new Date());
      } else {
        setError("Studio state is temporarily unavailable");
      }
    } catch {
      setError("Studio state is temporarily unavailable");
    } finally {
      setSummaryRefreshing(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [refresh]);

  useEffect(() => {
    let active = true;
    fetchCurrentUser().then((p) => {
      if (active) setUser(p.authenticated ? p.user : null);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    const isPlatformAdmin = (user?.roles || [])
      .some((role) => String(role).toUpperCase() === "PLATFORM_ADMIN");
    if (!isPlatformAdmin) {
      setDeploymentIdentity(null);
      return () => { active = false; };
    }
    setDeploymentIdentity(null);
    fetchDeploymentIdentity()
      .then((identity) => {
        if (active) setDeploymentIdentity(identity);
      })
      .catch(() => {
        if (active) setDeploymentIdentity({ status: "UNAVAILABLE" });
      });
    return () => { active = false; };
  }, [user]);

  useEffect(() => {
    let active = true;
    if (!canScheduleClasses(user)) {
      setSchedulingOptions(null);
      return () => { active = false; };
    }
    fetchStudioSchedulingOptions().then((options) => {
      if (!active) return;
      setSchedulingOptions(options);
      setScheduleForm((current) => ({
        ...current,
        event_type: current.event_type || options.event_types?.find((item) => item.available !== false)?.id || options.classes?.find((item) => item.available !== false)?.id || "",
        playlist_id: current.playlist_id || options.playlists?.find((item) => item.id === "Auto")?.id || "Auto",
        trainer_id: current.trainer_id || options.trainers?.[0]?.id || "",
        room_id: current.room_id || options.rooms?.[0]?.room_id || "",
        zone_id: current.zone_id || options.rooms?.[0]?.zone_id || "",
      }));
    }).catch((e) => {
      if (active) setScheduleError(e.message);
    });
    return () => { active = false; };
  }, [user]);

  const refreshNotifications = useCallback(async () => {
    if (!user) {
      setNotificationChannels([]);
      setNotificationSubscriptions([]);
      setNotificationAccount({ account: null, status: "not_configured" });
      setNotificationPublicUrl("");
      setNotificationsStale(false);
      setNotificationRefreshError("");
      setNotificationInviteUris({});
      setNotificationQrData({});
      return;
    }
    setNotificationsBusy(true);
    setNotificationsError("");
    try {
      const [channelPayload, subscriptionsPayload, accountPayload] = await Promise.all([
        fetchNotificationChannels(),
        fetchNotificationSubscriptions(),
        fetchNotificationAccount(),
      ]);
      if (!Array.isArray(channelPayload.channels) || !Array.isArray(subscriptionsPayload.subscriptions)) {
        throw new Error("Notification refresh returned an invalid response.");
      }
      const publicUrl = new URL(channelPayload.public_server_url);
      if (publicUrl.protocol !== "https:" || publicUrl.username || publicUrl.password || publicUrl.search || publicUrl.hash || publicUrl.pathname !== "/") {
        throw new Error("Canonical ntfy public URL is invalid.");
      }
      setNotificationChannels(channelPayload.channels);
      setNotificationSubscriptions(subscriptionsPayload.subscriptions);
      setNotificationPublicUrl(publicUrl.origin);
      const accountStatus = normalizeNotificationAccountStatus(accountPayload?.status);
      setNotificationAccount({ ...accountPayload, status: accountStatus });
      setAccountForm((current) => ({
        ...current,
        username: accountPayload?.account?.username || current.username,
        password: "",
        confirm_password: "",
      }));
      setNotificationsStale(false);
      setNotificationRefreshError("");
      const studioUserId = String(user?.id || user?.username || "");
      const nextInviteUris = Object.fromEntries((subscriptionsPayload.subscriptions || [])
        .filter((item) => String(item.user_id || "") === studioUserId)
        .map((item) => [String(item.channel || ""), notificationSubscriptionUri(item, publicUrl.origin)])
        .filter(([channelId, uri]) => channelId && uri));
      setNotificationInviteUris(accountStatus === "READY" ? nextInviteUris : {});
      if (accountStatus !== "READY") {
        setNotificationQrData({});
      }
      setSelectedChannelId((current) => {
        const candidates = (channelPayload.channels || []).filter((channel) => channel.eligible);
        if (current && candidates.some((channel) => channel.id === current)) return current;
        return candidates[0]?.id || "";
      });
    } catch (e) {
      setNotificationsStale(true);
      setNotificationRefreshError(e.message || "Could not refresh notification status.");
    } finally {
      setNotificationsBusy(false);
    }
  }, [user]);

  useEffect(() => {
    refreshNotifications();
  }, [refreshNotifications]);

  useEffect(() => {
    let active = true;
    async function buildQrCodes() {
      const entries = await Promise.all(Object.entries(notificationInviteUris).map(async ([channelId, uri]) => {
        try {
          const dataUrl = await QRCode.toDataURL(uri, { errorCorrectionLevel: "H", margin: 4, width: 240, color: { dark: "#000000", light: "#ffffff" } });
          return [channelId, dataUrl];
        } catch {
          return [channelId, "QR_ERROR"];
        }
      }));
      if (!active) return;
      const next = Object.fromEntries(entries.filter(([, value]) => Boolean(value)));
      setNotificationQrData(next);
      if (Object.values(next).includes("QR_ERROR")) {
        setNotificationsError("QR generation failed. Use the secure manual link below.");
      }
    }
    buildQrCodes();
    return () => { active = false; };
  }, [notificationInviteUris]);

  const refreshZoneOperation = useCallback(async () => {
    const payload = await fetchZoneAmbientOperation("Console");
    if (payload) setZoneOperation(payload.operation || null);
  }, []);

  useEffect(() => {
    refreshZoneOperation();
    const interval = setInterval(refreshZoneOperation, 5000);
    return () => clearInterval(interval);
  }, [refreshZoneOperation]);

  const handleLogout = async () => {
    const result = await logout();
    if (!result.success) {
      setError("Could not sign out. Please try again.");
      return;
    }
    window.location.href = PUBLIC_ENTRY_PATH;
  };

  // Derive key state
  const studio = summary.studio || FALLBACK_SUMMARY.studio;
  const branding = resolveBranding(summary.branding);
  const spatialSpaces = summary.spatial?.spaces || {};
  const cls = summary.class || FALLBACK_SUMMARY.class;
  const readiness = summary.readiness || FALLBACK_SUMMARY.readiness;
  const capabilities = readiness.capabilities || {};
  const blockingReasons = readiness.readiness_state?.blocking_reasons || readiness.blocking_reasons || [];
  const incidents = summary.incidents || [];
  const schedule = summary.schedule || [];

  // Deduplicate checked_in
  const checkedInRaw = cls.checked_in || [];
  const checkedInSet = new Set(checkedInRaw);
  const uniqueCheckedInCount = checkedInSet.size;
  const roster = [...new Set(cls.roster || [])];

  const eventId = cls.id;
  const eventState = cls.state || "NO_CLASS";
  const studioLifecycle = studio.lifecycle || "UNKNOWN";
  const receptionistOnly = hasRole(user, "RECEPTIONIST") && !hasRole(user, "ADMIN", "MANAGER", "OWNER");
  const portalHeader = resolvePortalHeader(branding, { receptionistOnly });
  const userCanControlAmbient = !receptionistOnly && hasRole(user, "RECEPTIONIST", "MANAGER", "ADMIN", "OWNER");
  const userCanViewCapabilitySurface = canViewCapabilitySurface(user);

  // Timer
  const { timerData, displaySeconds, timerKnown } = useClassTimer(eventId, eventState);

  // Dominant state computation
  function getDominantState() {
    return getDominantStudioState({
      eventId,
      eventState,
      hasAttention: incidents.length > 0 || Boolean(summary.fallback?.reason),
      receptionistOnly,
      scheduleCount: schedule.length,
      readinessReady: readiness.ready,
      studioLifecycle,
    });
  }

  function getDominantTone() {
    const ds = getDominantState();
    if (["CLASS RUNNING", "CLASS IN PROGRESS", "READY TO START", "UPCOMING CLASS", "CLASS SCHEDULED"].includes(ds)) return "ready";
    if (ds === "CLASS PAUSED" || ds === "CLASS STARTING") return "attention";
    if (ds === "CLASS COMPLETE") return "neutral";
    if (ds === "ATTENTION REQUIRED") return "danger";
    return "attention";
  }

  // Action handlers
  const doAction = async (fn, label) => {
    setActionBusy(true);
    setActionError("");
    try {
      await fn();
      await refresh();
    } catch (e) {
      setActionError(`${label} failed: ${e.message}`);
    } finally {
      setActionBusy(false);
    }
  };

  const handleStart = () => doAction(() => studioStartEvent(eventId), "Start");
  const handlePause = () => doAction(() => studioPauseEvent(eventId), "Pause");
  const handleResume = () => doAction(() => studioResumeEvent(eventId), "Resume");
  const handleFinish = () => doAction(() => studioFinishEvent(eventId), "Finish");

  const handleScheduleField = (field, value) => {
    setScheduleForm((current) => ({ ...current, [field]: value }));
  };

  const handleScheduleRoom = (value) => {
    const [roomId, zoneId] = value.split("::");
    setScheduleForm((current) => ({ ...current, room_id: roomId || "", zone_id: zoneId || "" }));
  };

  const handleScheduleClient = (clientId, checked) => {
    setScheduleForm((current) => ({
      ...current,
      client_ids: checked
        ? [...new Set([...current.client_ids, clientId])]
        : current.client_ids.filter((id) => id !== clientId),
    }));
  };

  const handleScheduleCreate = async (event) => {
    event.preventDefault();
    setScheduleBusy(true);
    setScheduleError("");
    setScheduleMessage("");
    try {
      const start = new Date(scheduleForm.scheduled_start);
      const end = new Date(scheduleForm.scheduled_end);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        throw new Error("Choose a class start and end date/time.");
      }
      if (!scheduleForm.trainer_id || !scheduleForm.room_id || !scheduleForm.zone_id || !scheduleForm.client_ids.length) {
        throw new Error("Choose a Trainer, Room, and at least one client.");
      }
      const result = await createStudioEvent({
        event_type: scheduleForm.event_type,
        playlist_id: scheduleForm.playlist_id,
        scheduled_start: start.toISOString(),
        scheduled_end: end.toISOString(),
        trainer_id: scheduleForm.trainer_id,
        client_ids: scheduleForm.client_ids,
        room_id: scheduleForm.room_id,
        zone_id: scheduleForm.zone_id,
      });
      setScheduleMessage(`Scheduled ${result.event?.event_type || "class"} · ${result.event?.id || ""}`);
      setScheduleCheckInEventId(result.event?.id || "");
      await refresh();
    } catch (e) {
      setScheduleError(e.message);
    } finally {
      setScheduleBusy(false);
    }
  };

  const handlePastClassesToggle = async () => {
    if (showPastClasses) {
      setShowPastClasses(false);
      return;
    }
    setPastClassesBusy(true);
    setPastClassesError("");
    try {
      const payload = await fetchPastStudioEvents();
      setPastClasses(payload.events || []);
      setShowPastClasses(true);
    } catch (error) {
      setPastClassesError(error.message);
    } finally {
      setPastClassesBusy(false);
    }
  };

  const focusExistingWorkflow = (workflow) => {
    if (workflow === "schedule" && receptionistOnly) {
      const shouldOpen = !showScheduleForm;
      setShowScheduleForm(shouldOpen);
      if (shouldOpen) schedulePanelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const target = workflow === "schedule" ? schedulePanelRef.current : checkInPanelRef.current;
    target?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleZoneOperation = async (action) => {
    setZoneOperationBusy(true);
    setZoneOperationError("");
    try {
      const result = action === "start"
        ? await startZoneAmbientOperation("Console")
        : await stopZoneAmbientOperation("Console");
      setZoneOperation(result.operation || null);
      if (!result.success) {
        setZoneOperationError(result.operation?.error || "Ambient media operation failed.");
      }
    } catch (e) {
      setZoneOperationError(e.message);
    } finally {
      await refreshZoneOperation();
      setZoneOperationBusy(false);
    }
  };

  const handleAccountSetup = async (event) => {
    event.preventDefault();
    setAccountBusy(true);
    setAccountError("");
    setAccountSuccess("");
    try {
      const result = await createNotificationAccount({
        username: accountForm.username,
        password: accountForm.password,
        confirm_password: accountForm.confirm_password,
      });
      if (String(result?.status || "").toUpperCase() !== "READY") {
        throw new Error("Provider identity is not verified and durable yet.");
      }
      setAccountSuccess("Notification account ready.");
      await refreshNotifications();
    } catch (e) {
      setAccountError(e.message || "Could not set up the notification account.");
    } finally {
      setAccountForm((current) => ({ ...current, password: "", confirm_password: "" }));
      setAccountBusy(false);
    }
  };

  const handleNotificationConnect = async (channelId) => {
    setNotificationActionBusy((current) => ({ ...current, [channelId]: true }));
    setNotificationsError("");
    try {
      const result = await createNotificationSubscription(channelId);
      if (String(result?.status || "").toUpperCase() === "ACTIVE"
        && String(result?.subscription?.lifecycle_state || "").toUpperCase() === "ACTIVE"
        && isSafeNotificationUri(result?.uri, notificationPublicUrl)) {
        setNotificationInviteUris((current) => ({ ...current, [channelId]: result.uri }));
      } else {
        throw new Error("The channel is not provider-verified ACTIVE.");
      }
      await refreshNotifications();
    } catch (e) {
      setNotificationsError(e.message);
      // A failed server reconciliation may still have durably recorded PENDING.
      // Refresh so the retry action reflects that canonical state.
      await refreshNotifications();
    } finally {
      setNotificationActionBusy((current) => ({ ...current, [channelId]: false }));
    }
  };

  const handleNotificationRevoke = async (subscriptionId, channelId) => {
    setNotificationActionBusy((current) => ({ ...current, [channelId]: true }));
    setNotificationsError("");
    try {
      await revokeNotificationSubscription(subscriptionId);
      setNotificationInviteUris((current) => {
        const next = { ...current };
        delete next[channelId];
        return next;
      });
      await refreshNotifications();
    } catch (e) {
      setNotificationsError(e.message);
    } finally {
      setNotificationActionBusy((current) => ({ ...current, [channelId]: false }));
    }
  };

  const dominantState = getDominantState();
  const dominantTone = getDominantTone();
  const ambientPlaying = zoneOperation?.desired_state === "PLAYING"
    && zoneOperation?.execution_state === "SUCCEEDED";
  const roomName = (roomId) => schedulingOptions?.rooms?.find((room) => room.room_id === roomId)?.name || label(roomId);
  const currentClassTrainerId = typeof cls.trainer === "object"
    ? cls.trainer?.trainer_id
    : cls.trainer_id || cls.trainer;
  const currentClassTrainer = (typeof cls.trainer === "object" && cls.trainer?.name)
    || schedulingOptions?.trainers?.find((trainer) => trainer.id === currentClassTrainerId)?.name;
  const currentClassAttendance = formatAttendanceSummary({
    checkedInCount: uniqueCheckedInCount,
    rosterCount: roster.length,
    rosterProvided: roster.length > 0,
  });
  const profileSummary = computeProfileCompletion(user);
  const profileDisplayName = receptionistOnly ? "Reception Desk" : (user?.display_name || user?.username || "Authenticated user");
  const profileInitials = profileDisplayName
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "H";
  const openProfile = () => {
    window.location.href = "/profile";
  };

  const runDoctor = async () => {
    if (doctorRunning) return;
    setDoctorRunning(true);
    setDoctorError("");
    setDoctorResult(null);
    try {
      const result = await runStudioDoctor();
      setDoctorResult(result);
    } catch (error) {
      setDoctorError(error?.response?.data?.error || "Doctor could not complete. Review system logs.");
    } finally {
      setDoctorRunning(false);
    }
  };

  const checkInPanel = (
    <section id="studio-checkin" className="studio-panel" ref={checkInPanelRef}>
      <div className="studio-panel-heading">
        <div className="studio-section-kicker">CLIENTS / CHECK-IN</div>
        <strong className="studio-count-display">
          {roster.length ? `${uniqueCheckedInCount} / ${roster.length}` : "Roster unavailable"}
        </strong>
      </div>
      <ul className="studio-roster-list">
        {roster.length ? roster.map((clientId) => (
          <RosterRow
            key={clientId}
            clientId={clientId}
            isCheckedIn={checkedInSet.has(clientId)}
            eventId={eventId}
            userCanCheckIn={canCheckIn(user)}
            onCheckedIn={refresh}
          />
        )) : <li className="studio-empty-row">No roster supplied for this event.</li>}
      </ul>
    </section>
  );

  return (
    <main className={`studio-console${receptionistOnly ? " studio-console-receptionist" : ""}`}>
      {/* ── Header ── */}
      <header className="studio-header studio-header-account">
        <div>
          <div className="studio-eyebrow">{portalHeader.eyebrow}</div>
          <h1 className="studio-title">{portalHeader.title}</h1>
          <p className="studio-subtitle">{portalHeader.subtitle}</p>
        </div>
        <div className="studio-header-right">
          <div className="studio-refresh-status">
            <div className="studio-muted" role="status" aria-live="polite">
              {summaryRefreshedAt
                ? `Last successful refresh: ${summaryRefreshedAt.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })}`
                : "Waiting for studio state…"}
            </div>
            <button
              className="studio-refresh-icon"
              type="button"
              onClick={refresh}
              disabled={summaryRefreshing}
              aria-label={summaryRefreshing ? "Refreshing studio state" : "Refresh studio state"}
              title={summaryRefreshing ? "Refreshing…" : "Refresh studio state"}
            >
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                className={summaryRefreshing ? "studio-refresh-spinning" : ""}
              >
                <path d="M20 7v5h-5" />
                <path d="M4.5 10a7.5 7.5 0 0 1 12.7-4.8L20 7" />
                <path d="M4 17v-5h5" />
                <path d="M19.5 14a7.5 7.5 0 0 1-12.7 4.8L4 17" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {user && (
      <StudioCockpit
        user={user}
        profileDisplayName={profileDisplayName}
        profileInitials={profileInitials}
        profileCompletion={profileSummary.available ? `${profileSummary.percent}% · ${profileSummary.label}` : "Profile details unavailable"}
        onEditProfile={openProfile}
        onSignOut={handleLogout}
        summary={summary}
        refreshError={error || applicationsError}
        applicationsStale={applicationsStale}
        applicationsLoading={applicationsLoading}
        stale={!summaryRefreshedAt || Date.now() - summaryRefreshedAt.getTime() > 15000}
        lastRefreshedAt={summaryRefreshedAt}
        deploymentIdentity={deploymentIdentity}
        onDoctor={runDoctor}
        doctorRunning={doctorRunning}
        doctorResult={doctorResult}
        doctorError={doctorError}
        onCloseDoctor={() => { setDoctorResult(null); setDoctorError(""); }}
        applications={applications}
        eventState={eventState}
        dominantState={dominantState}
        readiness={readiness}
        incidents={incidents}
        cls={cls}
        schedule={schedule}
        onSchedule={() => focusExistingWorkflow("schedule")}
        onStart={handleStart}
        onPause={handlePause}
        onResume={handleResume}
        onFinish={handleFinish}
        actionBusy={actionBusy}
        displaySeconds={displaySeconds}
        timerData={timerData}
        timerKnown={timerKnown}
      />
      )}
      <details className="studio-legacy-workflows">
        <summary>Operational workflows &amp; details</summary>

      {user && (user.roles || []).some((role) => ["PLATFORM_ADMIN", "ADMIN"].includes(String(role).toUpperCase())) && (
        <>
          {(user.roles || []).includes("PLATFORM_ADMIN") && <p className="studio-alert">Platform administrator · global HACaMS authority</p>}
          <div id="studio-access"><AccessAndAccounts showAccounts actorId={user.id} /></div>
        </>
      )}

      {error && <div className="studio-alert studio-alert-attention">{error}</div>}

      {/* ── Dominant Action Banner ── */}
      <section className={`studio-action-banner studio-action-banner-${dominantTone}`}>
        <div className="studio-action-left">
          <div className="studio-action-kicker">STUDIO STATUS</div>
          <div className="studio-action-state">{dominantState}</div>
          {receptionistOnly && cls.id && (
            <p className="studio-receptionist-status-detail">
              {roomName(cls.room_id || studio.room_id)} · {displayState(cls.type)} · {formatTime(cls.start_time)}
              {currentClassTrainer ? ` · ${currentClassTrainer}` : ""}
              {` · ${uniqueCheckedInCount}/${roster.length} checked in`}
            </p>
          )}
          {(eventState === "RUNNING" || eventState === "STARTED") && (
            <div className="studio-timer" aria-live="polite">{timerKnown ? formatElapsed(displaySeconds) : "Timer unavailable"}</div>
          )}
          {eventState === "PAUSED" && timerData && (
            <div className="studio-timer studio-timer-paused">PAUSED &nbsp;·&nbsp; {formatElapsed(timerData.elapsed_seconds || displaySeconds)}</div>
          )}
          {!readiness.ready && blockingReasons.length > 0 && eventState !== "RUNNING" && eventState !== "PAUSED" && eventState !== "FINISHED" && (
            <div className="studio-blocking-list">
              <div className="studio-blocking-count">{blockingReasons.length} action{blockingReasons.length > 1 ? "s" : ""} needed</div>
              {blockingReasons.map((r, i) => (
                <div key={i} className="studio-blocking-reason">• {r.reason || label(r.capability)}</div>
              ))}
            </div>
          )}
          {actionError && <div className="studio-action-error">{actionError}</div>}
        </div>
        <div className="studio-action-right">
          {/* Class control buttons */}
          {eventId && canControlClass(user) && eventState === "RUNNING" && (
            <>
              <button className="studio-btn-secondary" onClick={handlePause} disabled={actionBusy}>PAUSE</button>
              <button className="studio-btn-danger" onClick={handleFinish} disabled={actionBusy}>FINISH CLASS</button>
            </>
          )}
          {eventId && canControlClass(user) && eventState === "PAUSED" && (
            <>
              <button className="studio-btn-primary" onClick={handleResume} disabled={actionBusy}>RESUME</button>
              <button className="studio-btn-danger" onClick={handleFinish} disabled={actionBusy}>FINISH CLASS</button>
            </>
          )}
          {eventId && canControlClass(user) && eventState === "SCHEDULED" && cls.startable === true && readiness.ready && (
            <button className="studio-btn-primary studio-btn-start" onClick={handleStart} disabled={actionBusy}>
              {actionBusy ? "Starting…" : "▶  START CLASS"}
            </button>
          )}
          {eventId && canControlClass(user) && eventState === "SCHEDULED" && !cls.startable && (
            <div className="studio-not-ready-hint">Complete check-in and capability checks to enable START</div>
          )}
          {eventState === "FINISHED" && (
            <div className="studio-finished-notice">CLASS COMPLETE — Studio READY</div>
          )}
        </div>
      </section>

      <StudioApplications
        applications={applications}
        loading={applicationsLoading}
        error={applicationsError}
        stale={applicationsStale}
      />

      {receptionistOnly && (
        <div id="studio-spaces">
        <ReceptionistCapabilityControls
          spaces={spatialSpaces}
          capabilityCatalog={summary.spatial?.capabilities || {}}
          onScheduleClass={() => focusExistingWorkflow("schedule")}
          scheduleFormOpen={showScheduleForm}
          onCheckIn={() => focusExistingWorkflow("check_in")}
        />
        </div>
      )}

      {/* ── Primary Grid ── */}
      <div className="studio-grid studio-grid-primary">
        {/* Class Card */}
        {!receptionistOnly && <section id="studio-current-class" className="studio-class-card">
          <div className="studio-section-kicker">CURRENT CLASS</div>
          {cls.id ? (
            <>
              <h2 className="studio-class-type">{label(cls.type, "Class")}</h2>
              <div className="studio-class-meta">
                <div><span className="studio-meta-label">Start:</span> {formatTime(cls.start_time)}</div>
                <div><span className="studio-meta-label">End:</span> {formatTime(cls.end_time)}</div>
                <div><span className="studio-meta-label">Trainer:</span> {typeof cls.trainer === "object" ? (cls.trainer?.name || cls.trainer?.trainer_id || "—") : label(cls.trainer, "Not assigned")}</div>
                <div><span className="studio-meta-label">Event:</span> {label(cls.id)}</div>
                <div><span className="studio-meta-label">Room:</span> {roomName(cls.room_id || studio.room_id)}</div>
                <div><span className="studio-meta-label">State:</span> <StatusPill state={eventState} /></div>
              </div>
              <div className="studio-attendance-count">
                <span className="studio-count-big">{currentClassAttendance.numerator}</span>
                {currentClassAttendance.denominator !== null && (
                  <>
                    <span className="studio-count-sep">/</span>
                    <span className="studio-count-total">{currentClassAttendance.denominator}</span>
                  </>
                )}
                <span className="studio-count-label">
                  {currentClassAttendance.denominator !== null ? "clients checked in" : "No roster supplied"}
                </span>
              </div>
              <p className="studio-attendance-caption">{currentClassAttendance.caption}</p>
            </>
          ) : (
            <CurrentClassEmptyState />
          )}
        </section>}

        {/* Today Panel */}
        <section id="studio-schedule" className="studio-panel studio-today-panel" ref={schedulePanelRef}>
          <div className="studio-panel-heading">
            <div className="studio-section-kicker">TODAY'S SCHEDULE</div>
            {canScheduleClasses(user) && !receptionistOnly && (
              <button
                className="studio-btn-xs"
                type="button"
                onClick={() => scheduleFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
              >
                SCHEDULE EVENT
              </button>
            )}
          </div>
          {canScheduleClasses(user) && (!receptionistOnly || showScheduleForm) && (
            <form className="studio-schedule-form" ref={scheduleFormRef} onSubmit={handleScheduleCreate}>
              <div className="studio-section-kicker">SCHEDULE AN EVENT</div>
              <div className="studio-schedule-form-grid">
                <label className="studio-schedule-field">
                  <span>Event type</span>
                  <select
                    required
                    value={scheduleForm.event_type}
                    onChange={(e) => handleScheduleField("event_type", e.target.value)}
                    disabled={scheduleBusy || !(schedulingOptions?.event_types || schedulingOptions?.classes)?.length}
                  >
                    {(schedulingOptions?.event_types || schedulingOptions?.classes)?.map((item) => <option key={item.id} value={item.id} disabled={item.available === false}>{item.name}{item.available === false ? ` — ${item.unavailable_reason || "Playlist unavailable"}` : ""}</option>)}
                  </select>
                </label>
                <label className="studio-schedule-field">
                  <span>Event playlist</span>
                  <select
                    required
                    value={scheduleForm.playlist_id}
                    onChange={(e) => handleScheduleField("playlist_id", e.target.value)}
                    disabled={scheduleBusy || !schedulingOptions?.playlists?.length}
                  >
                    <option value="">Select Playlist</option>
                    {schedulingOptions?.playlists?.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
                  {schedulingOptions?.playlist_discovery_error && (
                    <span role="status">Playlist files could not be discovered. Auto programme is still available; refresh after the media source is restored.</span>
                  )}
                </label>
                <label className="studio-schedule-field">
                  <span>Trainer</span>
                  <select
                    required
                    value={scheduleForm.trainer_id}
                    onChange={(e) => handleScheduleField("trainer_id", e.target.value)}
                    disabled={scheduleBusy || !schedulingOptions?.trainers?.length}
                  >
                    <option value="">Select Trainer</option>
                    {schedulingOptions?.trainers?.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
                </label>
                <label className="studio-schedule-field">
                  <span>Start date/time</span>
                  <input type="datetime-local" required value={scheduleForm.scheduled_start} onChange={(e) => handleScheduleField("scheduled_start", e.target.value)} disabled={scheduleBusy} />
                </label>
                <label className="studio-schedule-field">
                  <span>End date/time</span>
                  <input type="datetime-local" required value={scheduleForm.scheduled_end} onChange={(e) => handleScheduleField("scheduled_end", e.target.value)} disabled={scheduleBusy} />
                </label>
                <label className="studio-schedule-field">
                  <span>Room</span>
                  <select
                    required
                    value={scheduleForm.room_id && scheduleForm.zone_id ? `${scheduleForm.room_id}::${scheduleForm.zone_id}` : ""}
                    onChange={(e) => handleScheduleRoom(e.target.value)}
                    disabled={scheduleBusy || !schedulingOptions?.rooms?.length}
                  >
                    <option value="">Select Room</option>
                    {schedulingOptions?.rooms?.map((item) => (
                      <option key={`${item.room_id}::${item.zone_id}`} value={`${item.room_id}::${item.zone_id}`}>{item.name}</option>
                    ))}
                  </select>
                </label>
              </div>
              <fieldset className="studio-schedule-clients" disabled={scheduleBusy || !schedulingOptions?.clients?.length}>
                <legend>Clients</legend>
                {schedulingOptions?.clients?.map((item) => (
                  <label key={item.id} className="studio-schedule-client-option">
                    <input
                      type="checkbox"
                      checked={scheduleForm.client_ids.includes(item.id)}
                      onChange={(e) => handleScheduleClient(item.id, e.target.checked)}
                    />
                    <span>{item.name}</span>
                  </label>
                ))}
              </fieldset>
              {scheduleError && <div className="studio-action-error">{scheduleError}</div>}
              {scheduleMessage && <div className="studio-schedule-success">{scheduleMessage}</div>}
              <div>
                <button className="studio-btn-primary" type="submit" disabled={scheduleBusy || !(schedulingOptions?.event_types || schedulingOptions?.classes)?.length || !schedulingOptions?.playlists?.length}>
                  {scheduleBusy ? "SCHEDULING…" : "SCHEDULE EVENT"}
                </button>
              </div>
            </form>
          )}
          <div className="studio-schedule-list">
            {schedule.length ? schedule.map((ev) => (
              <div className="studio-schedule-entry" key={ev.id}>
                <div className="studio-schedule-item">
                  <span className={`studio-mini-state studio-mini-state-${stateTone(ev.state)}`} />
                  <span className="studio-schedule-type">{label(ev.event_type, "Class")}</span>
                  <span className="studio-schedule-time">{formatTime(ev.scheduled_start)}</span>
                  <StatusPill state={ev.schedule_slot || ev.state} />
                </div>
                <div className="studio-schedule-details">
                  <span>Trainer: {schedulingOptions?.trainers?.find((item) => item.id === ev.trainer_id)?.name || label(ev.trainer_id, "Unassigned")}</span>
                  <span>Room: {roomName(ev.room_id || studio.room_id)}</span>
                  <span>{(ev.participant_ids || []).length} clients</span>
                  <span>{formatScheduleAttendance(ev.attendance)}</span>
                </div>
              </div>
            )) : <p className="studio-muted">No scheduled events.</p>}
          </div>
          {canScheduleClasses(user) && (
            <>
              {pastClassesError && <p className="studio-action-error">{pastClassesError}</p>}
              <button
                className="studio-btn-ghost studio-past-classes-toggle"
                type="button"
                onClick={handlePastClassesToggle}
                disabled={pastClassesBusy}
              >
                {pastClassesBusy ? "Loading…" : showPastClasses ? "Hide past classes" : "View past classes"}
              </button>
              {showPastClasses && (
                <section className="studio-past-classes" aria-label="Past classes, read only">
                  <div className="studio-section-kicker">PAST CLASSES · READ-ONLY</div>
                  {pastClasses.length ? pastClasses.map((event) => (
                    <div className="studio-schedule-item studio-past-class-row" key={event.id}>
                      <span className="studio-schedule-type">{label(event.event_type, "Class")}</span>
                      <span className="studio-schedule-time">{formatTime(event.scheduled_start)}</span>
                      <span>{roomName(event.room_id || studio.room_id)}</span>
                      <StatusPill state={event.state} />
                    </div>
                  )) : <p className="studio-muted">No past classes.</p>}
                </section>
              )}
            </>
          )}
        </section>
        {receptionistOnly && checkInPanel}
      </div>

      {/* ── Secondary Grid ── */}
      <div id="studio-capabilities" className="studio-grid studio-grid-secondary">
        {/* Capabilities */}
        {canAdminActions(user) && (
          <section className="studio-panel">
            <div className="studio-panel-heading">
              <div className="studio-section-kicker">ROOM CAPABILITIES</div>
              <span className="studio-muted">Required for class start</span>
            </div>
            <ul className="studio-capability-list">
              {Object.keys(capabilities).length ? Object.entries(capabilities).map(([name, value]) => (
                <CapabilityRow
                  key={name}
                  name={name}
                  value={value}
                  onProbe={refresh}
                  onRecover={refresh}
                  userCanAdmin={canAdminActions(user)}
                />
              )) : <li className="studio-muted">No capability state reported.</li>}
            </ul>
          </section>
        )}

        {userCanControlAmbient && (
          <section id="studio-ambient" className="studio-panel studio-ambient-panel">
            <div className="studio-panel-heading">
              <div className="studio-section-kicker">CONSOLE AMBIENT MEDIA</div>
              <StatusPill state={zoneOperation?.execution_state || "STOPPED"} />
            </div>
            <p className="studio-muted">{zoneOperation?.media_reference || "studio-ambient.m3u"}</p>
            {zoneOperationError && <div className="studio-action-error">{zoneOperationError}</div>}
            <div className="studio-cap-actions">
              <button
                className="studio-btn-primary"
                onClick={() => handleZoneOperation("start")}
                disabled={zoneOperationBusy || ambientPlaying}
              >
                START AMBIENT
              </button>
              <button
                className="studio-btn-secondary"
                onClick={() => handleZoneOperation("stop")}
                disabled={zoneOperationBusy || !ambientPlaying}
              >
                STOP AMBIENT
              </button>
            </div>
          </section>
        )}

        {!receptionistOnly && checkInPanel}
      </div>

      {!receptionistOnly && userCanViewCapabilitySurface && (
        <div id="studio-spaces">
        <ReceptionistCapabilityControls
          spaces={spatialSpaces}
          capabilityCatalog={summary.spatial?.capabilities || {}}
          onScheduleClass={() => focusExistingWorkflow("schedule")}
          scheduleFormOpen={showScheduleForm}
          onCheckIn={() => focusExistingWorkflow("check_in")}
        />
        </div>
      )}

      <span id="studio-attention" className="cockpit-anchor-target" />
      {/* ── Attention Panel ── */}
      {(incidents.length > 0 || summary.fallback?.reason) && (
        <section id="studio-attention-panel" className="studio-panel studio-attention-panel">
          <div className="studio-section-kicker">ROOM ATTENTION REQUIRED</div>
          {incidents.map((incident, i) => (
            <div className="studio-reason" key={i}>
              <strong>INCIDENT / {label(incident.capability)}</strong>
              <span>{label(incident.message, "Open Studio incident")}</span>
              <span className="studio-muted">CLASS REMAINS RUNNING</span>
            </div>
          ))}
          {summary.fallback?.reason && (
            <div className="studio-reason">
              <strong>FALLBACK / {label(summary.fallback.mode)}</strong>
              <span>{label(summary.fallback.reason)}</span>
            </div>
          )}
        </section>
      )}

      {/* ── Diagnostics (collapsible) ── */}
      {canAdminActions(user) && (
        <section id="studio-diagnostics" className="studio-diagnostics">
          <button
            className="studio-diagnostics-toggle"
            onClick={() => setShowDiagnostics((v) => !v)}
            aria-expanded={showDiagnostics}
          >
            {showDiagnostics ? "▲" : "▼"} System Details / Diagnostics / Audit
          </button>
          {showDiagnostics && (
            <div className="studio-diagnostics-body">
              {doctorError && <div className="studio-alert studio-alert-attention" role="alert">{doctorError}</div>}
              {doctorResult && (
                <section className="studio-doctor-report" aria-labelledby="studio-doctor-report-title">
                  <div className="studio-panel-heading">
                    <div>
                      <div className="studio-section-kicker">READ-ONLY DOCTOR REPORT</div>
                      <h3 id="studio-doctor-report-title">Overall status: {doctorResult.overall_status}</h3>
                    </div>
                    <span className="studio-muted">Rev {label(doctorResult.state_revision, "—")}</span>
                  </div>
                  <p className="studio-muted">Generated {doctorResult.generated_at ? new Date(doctorResult.generated_at).toLocaleString() : "—"}. No remediation commands were run.</p>
                  <div className="studio-doctor-checks">
                    {(doctorResult.checks || []).map((check, index) => (
                      <div className={"studio-doctor-check studio-doctor-check-" + String(check.status || "UNKNOWN").toLowerCase()} key={check.name + index}>
                        <strong>{check.name}</strong>
                        <span>{check.status}</span>
                        <small>{check.detail}</small>
                      </div>
                    ))}
                  </div>
                </section>
              )}
              <div><strong>state_authoritative:</strong> {String(summary.state_authoritative)}</div>
              <div><strong>state_revision:</strong> {label(summary.state_revision, "unknown")}</div>
              <div><strong>studio_id:</strong> {label(studio.id)}</div>
              <div><strong>event_id:</strong> {label(eventId)}</div>
              <div><strong>studio lifecycle:</strong> {studioLifecycle}</div>
              <div><strong>event state:</strong> {eventState}</div>
              <div><strong>raw checked_in:</strong> [{checkedInRaw.join(", ")}]</div>
              <div><strong>unique checked_in:</strong> {uniqueCheckedInCount}</div>
            </div>
          )}
        </section>
      )}

      </details>

      <footer className="studio-footer">
        <span>HACaMS Smart Studio — Authoritative State</span>
        <span>Rev {label(summary.state_revision, "—")}</span>
        <DeploymentStamp user={user} identity={deploymentIdentity} />
      </footer>
    </main>
  );
}
